/**
 * TLH — <tlh-upsell-carousel>
 * Carrousel « You may also like » du drawer panier.
 *
 * - Au doigt : défilement natif avec accroche carte par carte (CSS scroll-snap).
 * - À la souris : deux flèches précédent / suivant, masquées aux extrémités.
 * - Custom element : il se ré-initialise seul à chaque re-rendu du drawer
 *   (Section Rendering API) sans aucun câblage supplémentaire.
 */
class TlhUpsellCarousel extends HTMLElement {
  connectedCallback() {
    // Pare-feu : une erreur ici ne doit jamais casser le panier (les cartes restent
    // utilisables au doigt / a la molette, seules les fleches manqueraient)
    try {
      this.#init();
    } catch (err) {
      /* carrousel sans fleches */
    }
  }

  #init() {
    this.track = this.querySelector('[data-tlh-upsell-track]');
    if (!this.track || this.dataset.ready === '1') return;
    this.dataset.ready = '1';

    // Flèches uniquement pour les appareils à pointeur fin (souris / trackpad)
    if (!window.matchMedia('(pointer: fine)').matches) return;

    this.prev = this.#button('prev', 'Previous');
    this.next = this.#button('next', 'Next');
    this.append(this.prev, this.next);

    this.#update();
    this.track.addEventListener('scroll', () => this.#update(), { passive: true });
    if ('ResizeObserver' in window) {
      this.ro = new ResizeObserver(() => this.#update());
      this.ro.observe(this.track);
    }
  }

  disconnectedCallback() {
    this.ro?.disconnect();
  }

  #button(dir, label) {
    const b = document.createElement('button');
    b.type = 'button';
    b.className = `tlh-upsell__arrow tlh-upsell__arrow--${dir}`;
    b.setAttribute('aria-label', label);
    b.innerHTML =
      '<svg viewBox="0 0 16 16" width="14" height="14" aria-hidden="true" focusable="false">' +
      '<path d="M6 3l5 5-5 5" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/></svg>';
    b.addEventListener('click', () => this.#scroll(dir === 'next' ? 1 : -1));
    return b;
  }

  #step() {
    const card = this.track.querySelector('.tlh-upsell__card');
    if (!card) return this.track.clientWidth * 0.8;
    const gap = parseFloat(getComputedStyle(this.track).columnGap || getComputedStyle(this.track).gap) || 0;
    // On avance de deux cartes à la fois : le nombre visible dans le drawer
    return (card.getBoundingClientRect().width + gap) * 2;
  }

  #scroll(direction) {
    this.track.scrollBy({ left: direction * this.#step(), behavior: 'smooth' });
  }

  #update() {
    if (!this.prev || !this.next) return;
    const t = this.track;
    const card = t.querySelector('.tlh-upsell__media');
    if (card) this.style.setProperty('--tlh-upsell-media', `${card.offsetHeight}px`);
    const max = t.scrollWidth - t.clientWidth;
    const atStart = t.scrollLeft <= 2;
    const atEnd = t.scrollLeft >= max - 2;
    this.prev.hidden = atStart;
    this.next.hidden = atEnd || max <= 2;
  }
}

if (!customElements.get('tlh-upsell-carousel')) {
  customElements.define('tlh-upsell-carousel', TlhUpsellCarousel);
}
